
import {
  User,
  CustomDeck,
  MasterList,
  TournamentConfig,
  RoundConfig,
  SelectedTrickReference,
  HomeAnnouncementConfig,
  OnlineAccessConfig,
  OfficialWeChatLiveCodeConfig,
  OfficialWeChatQrCodeEntry,
  Kwc2026TeamupEntry,
  Kwc2026TeamupStatus
} from './types';
import { createEmptyCko2026WarmupProfile, normalizeCko2026WarmupProfile } from './cko2026Warmup';
import { isLocalBackend, phpApiUrl, phpAuthHeaders } from './apiEnv';
import { 
    KWC2025_TRICKS, 
    KWC2026_TRICKS,
    STARCITY_WINTER_2025_TRICKS,
    CKO2025_TRICKS,
    CKO2026_TRICKS,
    BKC_2025_BEGINNER_LV1,
    BKC_2025_BEGINNER_LV2,
    BKC_2025_BEGINNER_LV1_2,
    BKC_2025_ADVANCED_LV1,
    BKC_2025_ADVANCED_LV2,
    BKC_2025_ADVANCED_LV1_2,
    SKC_2026_NOVICE,
    SKC_2026_ADVANCED,
    SKC_2026_PRO
} from './constants';

const GUEST_UID = 'guest-local';
const GUEST_STORAGE_KEY = 'kendama_guest_data';
const LOCAL_HOME_ANNOUNCEMENT_STORAGE_KEY = 'kendama-local-home-announcement-config';
const LOCAL_ONLINE_ACCESS_STORAGE_KEY = 'kendama-local-online-access-config';

export const DEFAULT_TOURNAMENT_MASTER_LISTS: MasterList[] = [
  { id: 'default', name: 'Default List', tricks: [] },
  { id: 'kwc2025', name: 'KWC 2025', isPreset: true, tricks: KWC2025_TRICKS },
  { id: 'kwc2026', name: 'KWC 2026', isPreset: true, tricks: KWC2026_TRICKS },
  { id: 'scw2025', name: '星城剑客2025冬季赛附表', isPreset: true, tricks: STARCITY_WINTER_2025_TRICKS },
  { id: 'cko2025', name: '2025CKO中国剑玉杯', isPreset: true, tricks: CKO2025_TRICKS },
  { id: 'cko2026', name: '2026 CKO 官方招式总表', isPreset: true, tricks: CKO2026_TRICKS }
];

// 统一的后端请求：本地开发走 Node 的 /api 路径；网页/原生 App 走 PHP 接口。
// 原生 App 会使用线上绝对地址 + X-Kendama-Auth token（跨域无法依赖 Cookie）。
const apiRequest = (localPath: string, phpAction: string, init: RequestInit = {}): Promise<Response> => {
  const useLocal = isLocalBackend();
  const url = useLocal ? localPath : phpApiUrl(phpAction);
  const headers = phpAuthHeaders((init.headers as Record<string, string>) || undefined);
  return fetch(url, { ...init, headers });
};

export const OFFICIAL_WECHAT_QR_ROTATION_INTERVAL_DAYS = 6;
export const OFFICIAL_WECHAT_QR_ROTATION_MS = OFFICIAL_WECHAT_QR_ROTATION_INTERVAL_DAYS * 24 * 60 * 60 * 1000;

const decodeLegacyUHexString = (s: string) => {
  if (typeof s !== 'string') return s;
  
  let decoded = s;
  
  // 1. First, try standard URL decoding
  try {
      decoded = decodeURIComponent(decoded);
  } catch (e) {
      try {
          decoded = unescape(decoded);
      } catch (e2) {}
  }

  // 2. Decode raw unicode escape sequences: \uXXXX, %uXXXX, or raw uXXXX
  try {
      decoded = decoded.replace(/(?:\\u|%u|u)([0-9a-fA-F]{4})/g, (match, hex) => {
          return String.fromCharCode(parseInt(hex, 16));
      });
  } catch(e) {}

  // 3. A second pass of URL decoding might be needed if the unicode decode produced URI encoded characters
  try {
      decoded = decodeURIComponent(decoded);
  } catch (e) {
      try {
          decoded = unescape(decoded);
      } catch (e2) {}
  }

  // 4. Sometimes they are triple-encoded or nested, so we do one more unicode pass just in case
  try {
      decoded = decoded.replace(/(?:\\u|%u|u)([0-9a-fA-F]{4})/g, (match, hex) => {
          return String.fromCharCode(parseInt(hex, 16));
      });
  } catch(e) {}

  return decoded;
};

const decodeLegacyUHexDeep = (value: any): { changed: boolean } => {
  if (!value || typeof value !== 'object') return { changed: false };

  let changed = false;

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const v = value[i];
      if (typeof v === 'string') {
        const d = decodeLegacyUHexString(v);
        if (d !== v) {
          value[i] = d;
          changed = true;
        }
      } else if (v && typeof v === 'object') {
        if (decodeLegacyUHexDeep(v).changed) changed = true;
      }
    }
    return { changed };
  }

  for (const k of Object.keys(value)) {
    // DO NOT deep decode the tournamentData preset lists, let them come from constants.ts directly.
    if (k === 'masterLists' || k === 'tournamentData') {
        continue;
    }
    const v = value[k];
    if (typeof v === 'string') {
      const d = decodeLegacyUHexString(v);
      if (d !== v) {
        value[k] = d;
        changed = true;
      }
    } else if (v && typeof v === 'object') {
      if (decodeLegacyUHexDeep(v).changed) changed = true;
    }
  }

  return { changed };
};

export const createInitialTournamentData = (): TournamentConfig => ({
    masterLists: DEFAULT_TOURNAMENT_MASTER_LISTS.map(list => ({
      ...list,
      tricks: list.tricks.map(trick => ({ ...trick }))
    })),
    prelim1Configs: [{ id: 'p1-default', name: '初赛 1 - 配置 A', selectedTricks: [] }],
    prelim2Configs: [{ id: 'p2-default', name: '初赛 2 - 配置 A', selectedTricks: [] }],
    finalConfigs: [{ id: 'f1-default', name: '决赛配置 1', selectedTricks: [] }]
});

const cloneMasterLists = (lists: MasterList[]) => lists.map(list => ({
  ...list,
  tricks: Array.isArray(list.tricks) ? list.tricks.map(trick => ({ ...trick })) : []
}));

const cloneCustomDecks = (decks: CustomDeck[]) => decks.map(deck => ({
  ...deck,
  tricks: Array.isArray(deck.tricks) ? deck.tricks.map(trick => String(trick)) : []
}));

const sanitizeManagedMasterLists = (lists: unknown): MasterList[] => {
  if (!Array.isArray(lists) || lists.length === 0) return [];

  return lists
    .map((list: any, index) => {
      const fallbackId = index === 0 ? 'default' : `managed-${Date.now()}-${index}`;
      const rawId = String(list?.id || fallbackId).trim();
      const normalizedId = rawId || fallbackId;
      const rawName = String(list?.name || `Master List ${index + 1}`).trim();
      const tricks = Array.isArray(list?.tricks)
        ? list.tricks.map((trick: any, trickIndex: number) => ({
            id: String(trick?.id || `${normalizedId}-trick-${trickIndex + 1}`),
            name: String(trick?.name || '').trim(),
            score: Number.isFinite(Number(trick?.score)) ? Number(trick.score) : 0
          })).filter((trick: any) => trick.name)
        : [];

      return {
        id: normalizedId,
        name: rawName || `Master List ${index + 1}`,
        isPreset: normalizedId === 'default' ? undefined : true,
        tricks
      } as MasterList;
    })
    .filter((list, index, arr) => !!list.name && arr.findIndex(item => item.id === list.id) === index);
};

const resolveManagedTournamentMasterLists = (managedLists?: MasterList[] | null) => {
  const sanitizedManagedLists = sanitizeManagedMasterLists(managedLists);
  if (sanitizedManagedLists.length === 0) {
    return cloneMasterLists(DEFAULT_TOURNAMENT_MASTER_LISTS);
  }

  if (!sanitizedManagedLists.some(list => list.id === 'default')) {
    sanitizedManagedLists.unshift({ id: 'default', name: 'Default List', tricks: [] });
  }

  return sanitizedManagedLists.map(list => ({
    ...list,
    isPreset: list.id === 'default' ? undefined : true
  }));
};

export const mergeManagedTournamentMasterListsIntoUser = (
  user: User,
  managedMasterLists?: MasterList[] | null
): User => {
  const mergedUser = JSON.parse(JSON.stringify(user)) as User;
  const freshData = createInitialTournamentData();
  freshData.masterLists = resolveManagedTournamentMasterLists(managedMasterLists);

  if (!mergedUser.tournamentData) {
    mergedUser.tournamentData = freshData;
    return mergedUser;
  }

  const existingMasterLists = Array.isArray(mergedUser.tournamentData.masterLists)
    ? mergedUser.tournamentData.masterLists
    : [];
  const managedListIds = new Set(freshData.masterLists.map(list => list.id));

  // Preserve only true user-created custom lists; global lists always come from managed defaults.
  const customLists = existingMasterLists.filter((list: any) => !managedListIds.has(list.id) && !list.isPreset);

  customLists.forEach((list: any) => {
    if (typeof list.name === 'string') {
      list.name = decodeLegacyUHexString(list.name);
    }
    if (Array.isArray(list.tricks)) {
      list.tricks.forEach((trick: any) => {
        if (typeof trick.name === 'string') {
          trick.name = decodeLegacyUHexString(trick.name);
        }
      });
    }
  });

  const decodeConfigs = (configs: any[] | undefined, fallback: RoundConfig[]) => {
    const normalizedConfigs = Array.isArray(configs) && configs.length > 0 ? configs : fallback;
    normalizedConfigs.forEach(config => {
      if (config.name && typeof config.name === 'string') {
        config.name = decodeLegacyUHexString(config.name);
      }
    });
    return normalizedConfigs;
  };

  mergedUser.tournamentData = {
    ...mergedUser.tournamentData,
    prelim1Configs: decodeConfigs(mergedUser.tournamentData.prelim1Configs, freshData.prelim1Configs),
    prelim2Configs: decodeConfigs(mergedUser.tournamentData.prelim2Configs, freshData.prelim2Configs),
    finalConfigs: decodeConfigs(mergedUser.tournamentData.finalConfigs, freshData.finalConfigs),
    masterLists: [...cloneMasterLists(freshData.masterLists), ...customLists]
  };

  return mergedUser;
};

export const fetchManagedTournamentMasterLists = async (signal?: AbortSignal): Promise<MasterList[] | null> => {
  if (typeof window === 'undefined') return null;

  try {
    const response = await apiRequest(
      '/api/tournament/master-lists',
      'get_tournament_master_lists',
      { signal }
    );
    if (!response.ok) return null;

    const data = await response.json();
    return sanitizeManagedMasterLists(data.masterLists);
  } catch (error) {
    if ((error as Error)?.name === 'AbortError' || signal?.aborted) {
      return null;
    }
    console.error('Fetch managed tournament master lists error:', error);
    return null;
  }
};

export const fetchManagedDrawPresetDecks = async (signal?: AbortSignal): Promise<CustomDeck[] | null> => {
  if (typeof window === 'undefined') return null;

  try {
    const response = await apiRequest(
      '/api/draw/preset-decks',
      'get_draw_preset_decks',
      { signal }
    );
    if (!response.ok) return null;

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return null;

    const data = await response.json();
    return sanitizeManagedDrawPresetDecks(data.presetDecks);
  } catch (error) {
    if ((error as Error)?.name === 'AbortError' || signal?.aborted) {
      return null;
    }
    console.error('Fetch managed draw preset decks error:', error);
    return null;
  }
};

export const mergeManagedDrawPresetDecksIntoUser = (
  user: User,
  managedDrawPresetDecks?: CustomDeck[] | null
): User => {
  const mergedUser = JSON.parse(JSON.stringify(user)) as User;
  const freshPresetDecks = resolveManagedDrawPresetDecks(managedDrawPresetDecks);
  const managedDeckIds = new Set(freshPresetDecks.map(deck => deck.id));
  const existingCustomDecks = Array.isArray(mergedUser.customDecks) ? mergedUser.customDecks : [];

  const customDecks = existingCustomDecks.filter(deck => !managedDeckIds.has(deck.id));
  mergedUser.customDecks = [...customDecks, ...cloneCustomDecks(freshPresetDecks)];

  return mergedUser;
};

const sanitizeOfficialWeChatQrCodeEntry = (
  entry: unknown,
  index: number
): OfficialWeChatQrCodeEntry | null => {
  if (!entry || typeof entry !== 'object') return null;

  const rawImageUrl = typeof (entry as OfficialWeChatQrCodeEntry).imageUrl === 'string'
    ? (entry as OfficialWeChatQrCodeEntry).imageUrl.trim()
    : '';
  if (!rawImageUrl) return null;

  const imageUrl = rawImageUrl.startsWith('data:image/')
    ? rawImageUrl
    : rawImageUrl;
  const id = String((entry as OfficialWeChatQrCodeEntry).id || `official-wechat-qr-${index + 1}`).trim() || `official-wechat-qr-${index + 1}`;
  const createdAt = Number.isFinite(Number((entry as OfficialWeChatQrCodeEntry).createdAt))
    ? Number((entry as OfficialWeChatQrCodeEntry).createdAt)
    : Date.now();

  return {
    id,
    imageUrl,
    createdAt
  };
};

export const sanitizeOfficialWeChatLiveCodeConfig = (
  config: unknown
): OfficialWeChatLiveCodeConfig => {
  const rawConfig = config && typeof config === 'object' ? (config as Partial<OfficialWeChatLiveCodeConfig>) : {};
  const entries = Array.isArray(rawConfig.entries)
    ? rawConfig.entries
        .map((entry, index) => sanitizeOfficialWeChatQrCodeEntry(entry, index))
        .filter((entry): entry is OfficialWeChatQrCodeEntry => !!entry)
    : [];
  const updatedAt = Number.isFinite(Number(rawConfig.updatedAt))
    ? Number(rawConfig.updatedAt)
    : Date.now();
  const rotationStartedAt = Number.isFinite(Number(rawConfig.rotationStartedAt))
    ? Number(rawConfig.rotationStartedAt)
    : updatedAt;

  return {
    entries,
    rotationStartedAt,
    intervalDays: OFFICIAL_WECHAT_QR_ROTATION_INTERVAL_DAYS,
    updatedAt
  };
};

export const getActiveOfficialWeChatQrCode = (
  config: OfficialWeChatLiveCodeConfig | null | undefined,
  now: number = Date.now()
) => {
  const normalizedConfig = sanitizeOfficialWeChatLiveCodeConfig(config);
  const totalEntries = normalizedConfig.entries.length;
  if (totalEntries === 0) {
    return {
      entry: null,
      activeIndex: -1,
      totalEntries: 0,
      startedAt: normalizedConfig.rotationStartedAt,
      endsAt: normalizedConfig.rotationStartedAt
    };
  }

  const safeStart = normalizedConfig.rotationStartedAt > 0 ? normalizedConfig.rotationStartedAt : normalizedConfig.updatedAt;
  const elapsed = Math.max(0, now - safeStart);
  const cycleIndex = Math.floor(elapsed / OFFICIAL_WECHAT_QR_ROTATION_MS);
  const activeIndex = cycleIndex % totalEntries;
  const startedAt = safeStart + cycleIndex * OFFICIAL_WECHAT_QR_ROTATION_MS;

  return {
    entry: normalizedConfig.entries[activeIndex] || null,
    activeIndex,
    totalEntries,
    startedAt,
    endsAt: startedAt + OFFICIAL_WECHAT_QR_ROTATION_MS
  };
};

export const fetchOfficialWeChatLiveCodeConfig = async (
  signal?: AbortSignal
): Promise<OfficialWeChatLiveCodeConfig | null> => {
  if (typeof window === 'undefined') return null;

  try {
    const response = await apiRequest(
      '/api/official-wechat-live-code',
      'get_official_wechat_live_code',
      { signal }
    );
    if (!response.ok) return null;

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return null;

    const data = await response.json();
    return sanitizeOfficialWeChatLiveCodeConfig(data.config);
  } catch (error) {
    if ((error as Error)?.name === 'AbortError' || signal?.aborted) {
      return null;
    }
    console.error('Fetch official WeChat live code config error:', error);
    return null;
  }
};

export const saveOfficialWeChatLiveCodeConfig = async (
  config: OfficialWeChatLiveCodeConfig
): Promise<OfficialWeChatLiveCodeConfig> => {
  const response = await apiRequest(
    '/api/admin/official-wechat-live-code',
    'admin_save_official_wechat_live_code',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ config })
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '保存微信群活码失败');
  }

  return sanitizeOfficialWeChatLiveCodeConfig(data.config);
};

export const sanitizeHomeAnnouncementConfig = (
  config: unknown
): HomeAnnouncementConfig => {
  const rawConfig = config && typeof config === 'object' ? (config as Partial<HomeAnnouncementConfig>) : {};
  const title = typeof rawConfig.title === 'string' ? rawConfig.title : '';
  const content = typeof rawConfig.content === 'string' ? rawConfig.content : '';
  const updatedAt = Number.isFinite(Number(rawConfig.updatedAt))
    ? Number(rawConfig.updatedAt)
    : 0;
  const pushedAt = Number.isFinite(Number(rawConfig.pushedAt))
    ? Number(rawConfig.pushedAt)
    : 0;

  return {
    title,
    content,
    updatedAt,
    pushedAt
  };
};

const readLocalHomeAnnouncementConfig = (): HomeAnnouncementConfig | null => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(LOCAL_HOME_ANNOUNCEMENT_STORAGE_KEY);
    if (!raw) return sanitizeHomeAnnouncementConfig(null);
    return sanitizeHomeAnnouncementConfig(JSON.parse(raw));
  } catch {
    return sanitizeHomeAnnouncementConfig(null);
  }
};

const writeLocalHomeAnnouncementConfig = (config: HomeAnnouncementConfig) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    LOCAL_HOME_ANNOUNCEMENT_STORAGE_KEY,
    JSON.stringify(sanitizeHomeAnnouncementConfig(config))
  );
};

export const fetchHomeAnnouncementConfig = async (
  signal?: AbortSignal
): Promise<HomeAnnouncementConfig | null> => {
  if (typeof window === 'undefined') return null;

  const fallbackToLocalConfig = () => readLocalHomeAnnouncementConfig();

  const timeoutController = new AbortController();
  const timeoutId = signal ? null : window.setTimeout(() => timeoutController.abort(), 8000);
  const requestSignal = signal || timeoutController.signal;

  try {
    const response = await apiRequest(
      '/api/home-announcement',
      'get_home_announcement',
      { signal: requestSignal }
    );
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
    if (!response.ok) {
      return isLocalBackend() ? fallbackToLocalConfig() : null;
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return isLocalBackend() ? fallbackToLocalConfig() : null;
    }

    const data = await response.json();
    return sanitizeHomeAnnouncementConfig(data.config);
  } catch (error) {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
    if ((error as Error)?.name === 'AbortError' || signal?.aborted) {
      return isLocalBackend() ? fallbackToLocalConfig() : null;
    }
    console.error('Fetch home announcement config error:', error);
    return isLocalBackend() ? fallbackToLocalConfig() : null;
  }
};

export const saveHomeAnnouncementConfig = async (
  config: HomeAnnouncementConfig
): Promise<HomeAnnouncementConfig> => {
  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(() => timeoutController.abort(), 3000);

  try {
    const response = await apiRequest(
      '/api/admin/home-announcement',
      'admin_save_home_announcement',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config }),
        signal: timeoutController.signal
      }
    );
    window.clearTimeout(timeoutId);

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      if (isLocalBackend()) {
        const fallbackConfig = sanitizeHomeAnnouncementConfig(config);
        writeLocalHomeAnnouncementConfig(fallbackConfig);
        return fallbackConfig;
      }
      throw new Error(data?.error || '保存首页公告失败');
    }

    return sanitizeHomeAnnouncementConfig(data.config);
  } catch (error) {
    window.clearTimeout(timeoutId);
    if (isLocalBackend()) {
      const fallbackConfig = sanitizeHomeAnnouncementConfig(config);
      writeLocalHomeAnnouncementConfig(fallbackConfig);
      return fallbackConfig;
    }
    throw error;
  }
};

export const sanitizeOnlineAccessConfig = (config: unknown): OnlineAccessConfig => {
  const rawConfig = config && typeof config === 'object' ? (config as Partial<OnlineAccessConfig>) : {};
  return {
    adminOnly: Boolean(rawConfig.adminOnly),
    updatedAt: Number.isFinite(Number(rawConfig.updatedAt)) ? Number(rawConfig.updatedAt) : 0
  };
};

const readLocalOnlineAccessConfig = (): OnlineAccessConfig => {
  if (typeof window === 'undefined') return sanitizeOnlineAccessConfig(null);
  try {
    const raw = window.localStorage.getItem(LOCAL_ONLINE_ACCESS_STORAGE_KEY);
    if (!raw) return sanitizeOnlineAccessConfig(null);
    return sanitizeOnlineAccessConfig(JSON.parse(raw));
  } catch {
    return sanitizeOnlineAccessConfig(null);
  }
};

const writeLocalOnlineAccessConfig = (config: OnlineAccessConfig) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    LOCAL_ONLINE_ACCESS_STORAGE_KEY,
    JSON.stringify(sanitizeOnlineAccessConfig(config))
  );
};

// 服务器返回过的最后一次开关状态。读取失败时用它兜底，
// 而不是回退成写死的「关闭」——否则管理界面会把已经开启的开关显示成关闭。
const ONLINE_ACCESS_CACHE_KEY = 'kendama-online-access-cache';
// 这个接口在弱网 / 启动并发下经常要 1~3 秒，超时给足，避免误判成失败
const ONLINE_ACCESS_FETCH_TIMEOUT_MS = 10000;

const readOnlineAccessCache = (): OnlineAccessConfig | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ONLINE_ACCESS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const config = sanitizeOnlineAccessConfig(parsed);
    // 缓存里必须带服务器时间戳，才认为是一条可信的服务器快照
    return config.updatedAt > 0 ? config : null;
  } catch {
    return null;
  }
};

const writeOnlineAccessCache = (config: OnlineAccessConfig) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      ONLINE_ACCESS_CACHE_KEY,
      JSON.stringify(sanitizeOnlineAccessConfig(config))
    );
  } catch {
    // 本地存储不可用时忽略
  }
};

export type OnlineAccessConfigSource = 'server' | 'cache' | 'local' | 'default';

/**
 * 读取联机模式开关，并告诉调用方这个值是哪来的：
 * server = 服务器实时值；cache = 服务器上次的真实值（本次读取失败）；
 * local/default = 连缓存都没有时的兜底。
 */
export const fetchOnlineAccessConfigWithSource = async (
  signal?: AbortSignal
): Promise<{ config: OnlineAccessConfig; source: OnlineAccessConfigSource }> => {
  if (typeof window === 'undefined') {
    return { config: sanitizeOnlineAccessConfig(null), source: 'default' };
  }

  const attempt = async (): Promise<OnlineAccessConfig> => {
    const timeoutController = new AbortController();
    const timeoutId = signal ? null : window.setTimeout(() => timeoutController.abort(), ONLINE_ACCESS_FETCH_TIMEOUT_MS);
    try {
      const response = await apiRequest(
        '/api/online-access',
        'online_access',
        { signal: signal || timeoutController.signal }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) throw new Error('Non-JSON response');

      const data = await response.json();
      if (!data || data.success === false) throw new Error('Online access API returned failure');

      return sanitizeOnlineAccessConfig(data.config);
    } finally {
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    }
  };

  const fallback = (): { config: OnlineAccessConfig; source: OnlineAccessConfigSource } => {
    const cached = readOnlineAccessCache();
    if (cached) return { config: cached, source: 'cache' };
    const local = readLocalOnlineAccessConfig();
    if (local.updatedAt > 0) return { config: local, source: 'local' };
    return { config: sanitizeOnlineAccessConfig(null), source: 'default' };
  };

  try {
    const config = await attempt();
    writeOnlineAccessCache(config);
    return { config, source: 'server' };
  } catch (error) {
    console.warn('Fetch online access config failed, retrying:', error);
  }

  if (signal?.aborted) return fallback();

  // 启动阶段同时发起的请求多（会话/公告/总表），这个接口很容易被挤超时，重试一次通常就成
  try {
    const config = await attempt();
    writeOnlineAccessCache(config);
    return { config, source: 'server' };
  } catch (error) {
    console.warn('Fetch online access config failed again, falling back:', error);
  }

  return fallback();
};

export const fetchOnlineAccessConfig = async (
  signal?: AbortSignal
): Promise<OnlineAccessConfig> => (await fetchOnlineAccessConfigWithSource(signal)).config;

export const saveOnlineAccessConfig = async (
  config: OnlineAccessConfig
): Promise<OnlineAccessConfig> => {
  const withLocalFallback = () => {
    const fallbackConfig = sanitizeOnlineAccessConfig(config);
    writeLocalOnlineAccessConfig(fallbackConfig);
    writeOnlineAccessCache(fallbackConfig);
    return fallbackConfig;
  };

  // 这个接口在部分网络下要 1~3 秒，原来是 3 秒超时，很容易被误判成失败
  const attemptSave = async (timeoutMs: number): Promise<OnlineAccessConfig> => {
    const timeoutController = new AbortController();
    const timeoutId = window.setTimeout(() => timeoutController.abort(), timeoutMs);
    try {
      const response = await apiRequest(
        '/api/admin/online-access',
        'admin_save_online_access',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ config }),
          signal: timeoutController.signal
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || '保存联机模式设置失败');
      }
      const saved = sanitizeOnlineAccessConfig(data.config);
      // 保存成功后立刻记下服务器真实值，之后即使读接口超时也能显示正确状态
      writeOnlineAccessCache(saved);
      return saved;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  try {
    return await attemptSave(15000);
  } catch (error) {
    if (isLocalBackend()) return withLocalFallback();

    const isAbort = (error as Error)?.name === 'AbortError' || (error as Error)?.name === 'TimeoutError';
    if (!isAbort) throw error;

    // 超时/被中断时重试一次；服务端很可能已经写入成功
    try {
      return await attemptSave(15000);
    } catch {
      // 再读一次服务器真实值，避免界面显示和服务器不一致
      try {
        return await fetchOnlineAccessConfig();
      } catch {
        throw new Error('保存超时，请重试 / Save timed out, please retry.');
      }
    }
  }
};

const sanitizeKwc2026TeamupEntry = (entry: unknown): Kwc2026TeamupEntry | null => {
  if (!entry || typeof entry !== 'object') return null;

  const rawEntry = entry as Partial<Kwc2026TeamupEntry>;
  const status = rawEntry.status;
  if (
    status !== 'definitely-going'
    && status !== 'maybe-going'
    && status !== 'online-watching'
  ) {
    return null;
  }

  const userId = String(rawEntry.userId || '').trim();
  const displayName = String(rawEntry.displayName || '').trim();
  if (!userId || !displayName) return null;

  return {
    userId,
    displayName,
    avatarUrl: typeof rawEntry.avatarUrl === 'string' ? rawEntry.avatarUrl : '',
    status,
    note: typeof rawEntry.note === 'string' ? rawEntry.note : '',
    createdAt: Number.isFinite(Number(rawEntry.createdAt)) ? Number(rawEntry.createdAt) : 0,
    updatedAt: Number.isFinite(Number(rawEntry.updatedAt)) ? Number(rawEntry.updatedAt) : 0
  };
};

export const fetchKwc2026TeamupEntries = async (
  signal?: AbortSignal
): Promise<Kwc2026TeamupEntry[]> => {
  if (typeof window === 'undefined') return [];

  try {
    const response = await apiRequest(
      '/api/kwc2026/teamup',
      'get_kwc2026_teamup',
      { signal }
    );
    if (!response.ok) return [];

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return [];

    const data = await response.json();
    const entries = Array.isArray(data?.entries) ? data.entries : [];
    return entries
      .map((item: unknown) => sanitizeKwc2026TeamupEntry(item))
      .filter((item: Kwc2026TeamupEntry | null): item is Kwc2026TeamupEntry => !!item);
  } catch (error) {
    if ((error as Error)?.name === 'AbortError' || signal?.aborted) {
      return [];
    }
    console.error('Fetch KWC2026 teamup entries error:', error);
    return [];
  }
};

export const saveKwc2026TeamupEntry = async (
  input: { status: Kwc2026TeamupStatus; note?: string }
): Promise<Kwc2026TeamupEntry> => {
  const response = await apiRequest(
    '/api/kwc2026/teamup',
    'save_kwc2026_teamup',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input)
    }
  );

  const data = await response.json().catch(() => ({}));
  const savedEntry = sanitizeKwc2026TeamupEntry(data?.entry);
  if (!response.ok || !data?.success || !savedEntry) {
    throw new Error(data?.error || '保存 KWC 找队友信息失败');
  }

  return savedEntry;
};

const BKC_2025_PRESET_DECKS: CustomDeck[] = [
    { id: 'bkc-b-lv1', name: '2025BKC初级组LV1', tricks: BKC_2025_BEGINNER_LV1, folderId: 'folder_bkc2025' },
    { id: 'bkc-b-lv2', name: '2025BKC初级组LV2', tricks: BKC_2025_BEGINNER_LV2, folderId: 'folder_bkc2025' },
    { id: 'bkc-b-lv1-2', name: '2025BKC初级组LV1-2', tricks: BKC_2025_BEGINNER_LV1_2, folderId: 'folder_bkc2025' },
    { id: 'bkc-a-lv1', name: '2025BKC高级组LV1', tricks: BKC_2025_ADVANCED_LV1, folderId: 'folder_bkc2025' },
    { id: 'bkc-a-lv2', name: '2025BKC高级组LV2', tricks: BKC_2025_ADVANCED_LV2, folderId: 'folder_bkc2025' },
    { id: 'bkc-a-lv1-2', name: '2025BKC高级组LV1-2', tricks: BKC_2025_ADVANCED_LV1_2, folderId: 'folder_bkc2025' },
];

const SKC_2026_PRESET_DECKS: CustomDeck[] = [
    { id: 'skc-novice', name: '新手', tricks: SKC_2026_NOVICE, folderId: 'folder_skc2026' },
    { id: 'skc-advanced', name: '进阶', tricks: SKC_2026_ADVANCED, folderId: 'folder_skc2026' },
    { id: 'skc-pro', name: '专业', tricks: SKC_2026_PRO, folderId: 'folder_skc2026' },
];

const TAKEN_2026_BEGINNER = [
    '灯台，收\nLighthouse In',
    '飞机\nAirplane',
    '游泳\nSwing Spike',
    '世界一周\nAround The World',
    '天中杀\nDownspike',
    '玉中杀\nStunt Plane',
    '中皿，升龙止剑\nBase Cup, Rising Dragon, Spike',
    '棒球中皿\nBaseball Base Cup',
    '大皿，轨道大皿\nBig Cup, Orbit',
    '竹蜻蜓\nFlying Top',
    '空中飞人\nTrapeze',
    '大鸟止剑\nBird Spike',
];

const TAKEN_2026_INTERMEDIATE = [
    '美国一周\nAround USA',
    '鸟渡，地球收\nBird Over The Valley, 1 Turn Spike',
    '一回转月面，一回收\n1 Turn Lunar, Flip In',
    '一回转飞机，跳剑\n1 Turn Airplane, J Stick',
    'Tap灯台，持剑收\nTap Lighthouse, Tradespike',
    '游泳，离转地球\nSwing Spike, Whirlwind',
    '神枪手止剑，地球神枪手\nGunslinger Spike, Whirlslinger',
    '小丑止剑\nJuggle Spike',
    '飞机跳剑1.5换止剑\nAirplane, 1.5 Swap Spike',
    '竹马，一回转收\nStilt, Flip In',
    '中皿极意，止剑\nHandlestall, Spike',
    '转盘，止剑\nTurntable, Spike',
];

const TAKEN_2026_ADVANCED = [
    '两回转飞机，两跳剑\n2 Turn Airplane, Double J Stick',
    '丹麦一周\nAround Denmark',
    '极意一周\nAround Stall, 1 Turn Spike',
    '两回转灯台，灯上两回转，玉中杀快手\n2 Turn Lighthouse, Double Lighthouse Flip, Stuntplane Fasthand',
    '一回转cushion tap 小丑止剑\n1 Turn Cushion Tap Juggle Spike',
    '2tap2圈小丑止剑\n2 Tap 2 Kenflip Juggle Spike',
    '离转小丑-离转小丑止剑\nKenflip Juggle Kenflip Juggle Spike',
    '离转地球late止剑\nWhirlwind Late Flip',
    '梦月，月上一回，0.5小皿正月，月上一回，一回收\nBackward Inward Lunar Flip, Luna Flip, Flip In',
    '10小丑以上止剑\n10 Or More Juggle Spike',
    '快板天中杀，快板地球\nSwivel Downspike, Swivel Earth Turn Spike',
    '一回转灯台insta换1.5收\n1 Turn Lighthouse Insta Swap 1.5 Toss In',
    '烛台中皿，飞镖\nCandle Base Cup, Dart',
    '斧，斧上一回转，收\nAxe, Axe Flip, In',
    '游泳2神枪手，地球2神枪手\nDouble Gunslinger Spike, Double Whirlslinger',
    '飞机，三跳剑\nAirplane, 3 Turn J Stick',
    '飞机，1.5小丑大鸟，地球收\nAirplane, 1.5 Juggle Bird, 1 Turn Spike',
    '竹马渡，一回收\nStilt Over The Valley, Flip In',
    '安德鸟，燕返，持球收\nUnderbird, On/Off Underbird, Falling In',
    '月面，月上三回转，一回转收\nLunar, 3 Turn Lunar Flip, Flip In',
];

const TAKEN_2026_PRESET_DECKS: CustomDeck[] = [
    { id: 'taken-beginner', name: 'Beginner 初级组', tricks: TAKEN_2026_BEGINNER, folderId: 'folder_taken2026' },
    { id: 'taken-intermediate', name: 'Intermediate 中级组', tricks: TAKEN_2026_INTERMEDIATE, folderId: 'folder_taken2026' },
    { id: 'taken-advanced', name: 'Advanced 高级组', tricks: TAKEN_2026_ADVANCED, folderId: 'folder_taken2026' },
];

const CSK_2026_BEGINNER = [
    '大皿',
    '止剑',
    '简易中皿绕背',
    '简易月面转圈',
    '简易大鸟止剑',
    '倒奶',
    '提线抓剑',
    '传球',
    '悬停机',
    '竹蜻蜓',
    '掌中灯',
    '简易极意单程燕返',
];

const CSK_2026_INTERMEDIATE = [
    '世界一周',
    '灯塔收剑',
    '月面收剑',
    '欧洲一周',
    '神枪手止剑',
    '离转大皿止剑',
    '天中杀',
    '玉中杀',
    '飞机跳剑',
    '鸟渡',
    '小丑大皿止剑',
    '简易太阳极意止剑',
];

const CSK_2026_ADVANCED = [
    '丹麦一周',
    '中国一周',
    '极意一周',
    '月渡',
    '跳剑1.5小丑止剑',
    '马渡',
    '2tap小丑止剑',
    '离转小丑离转小丑止剑',
    '神枪手游泳神枪手地球',
    '飞行V玉中杀快手',
    '小丑止剑10',
    'insta灯小丑止剑',
];

const CSK_2026_PRESET_DECKS: CustomDeck[] = [
    { id: 'csk-beginner', name: '初级组', tricks: CSK_2026_BEGINNER, folderId: 'folder_csk2026' },
    { id: 'csk-intermediate', name: '中级组', tricks: CSK_2026_INTERMEDIATE, folderId: 'folder_csk2026' },
    { id: 'csk-advanced', name: '高级组', tricks: CSK_2026_ADVANCED, folderId: 'folder_csk2026' },
];

export const PRESET_FOLDERS = [
    { id: 'folder_bkc2025', name: '2025BKC北京剑玉杯' },
    { id: 'folder_skc2026', name: '2026SKC上海剑玉公开赛' },
    { id: 'folder_taken2026', name: '2026Taken剑玉杯' },
    { id: 'folder_csk2026', name: '2026CSK0夏季赛招式表' }
];

export const DEFAULT_DRAW_PRESET_DECKS: CustomDeck[] = [
  ...BKC_2025_PRESET_DECKS,
  ...SKC_2026_PRESET_DECKS,
  ...TAKEN_2026_PRESET_DECKS,
  ...CSK_2026_PRESET_DECKS
].map(deck => ({
  ...deck,
  isPreset: true
}));

const sanitizeManagedDrawPresetDecks = (decks: unknown): CustomDeck[] => {
  if (!Array.isArray(decks) || decks.length === 0) return [];

  return decks
    .map((deck: any, index) => {
      const fallbackId = `managed-draw-${Date.now()}-${index}`;
      const id = String(deck?.id || fallbackId).trim() || fallbackId;
      const name = String(deck?.name || `Draw Deck ${index + 1}`).trim() || `Draw Deck ${index + 1}`;
      const folderId = String(deck?.folderId || PRESET_FOLDERS[0]?.id || '').trim() || PRESET_FOLDERS[0]?.id;
      const tricks = Array.isArray(deck?.tricks)
        ? deck.tricks
            .map((trick: any) => String(trick || '').trim())
            .filter((trick: string) => !!trick)
        : [];

      return {
        id,
        name,
        folderId,
        isPreset: true,
        tricks
      } as CustomDeck;
    })
    .filter((deck, index, arr) => !!deck.name && arr.findIndex(item => item.id === deck.id) === index);
};

const resolveManagedDrawPresetDecks = (managedDecks?: CustomDeck[] | null) => {
  const sanitizedManagedDecks = sanitizeManagedDrawPresetDecks(managedDecks);
  if (sanitizedManagedDecks.length === 0) {
    return cloneCustomDecks(DEFAULT_DRAW_PRESET_DECKS);
  }

  return sanitizedManagedDecks.map(deck => ({
    ...deck,
    isPreset: true
  }));
};

const getRequiredPresetFolders = (managedDecks?: CustomDeck[] | null) => {
  const folderMap = new Map(PRESET_FOLDERS.map(folder => [folder.id, folder]));
  resolveManagedDrawPresetDecks(managedDecks).forEach(deck => {
    if (!deck.folderId) return;
    if (!folderMap.has(deck.folderId)) {
      folderMap.set(deck.folderId, {
        id: deck.folderId,
        name: deck.folderId
      });
    }
  });
  return Array.from(folderMap.values());
};

export const storage = {
  getUser: async (uid: string): Promise<User | null> => {
    if (!uid) return null;
    if (uid === GUEST_UID) {
        try {
            const localData = localStorage.getItem(GUEST_STORAGE_KEY);
            if (localData) return migrateUserData(JSON.parse(localData) as User);
            return null;
        } catch (e) {
            return null;
        }
    }
    
    try {
      const response = await apiRequest('/api/me', 'me');
      if (!response.ok) return null;
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return null;
      const data = await response.json();
      const managedMasterLists = await fetchManagedTournamentMasterLists();
      const managedDrawPresetDecks = await fetchManagedDrawPresetDecks();
      return migrateUserData(data.user, managedMasterLists, managedDrawPresetDecks);
    } catch (error) {
      console.error("API getUser error:", error);
      return null;
    }
  },

  save: async (user: User) => {
    if (!user || !user.uid) return false;
    if (user.uid === GUEST_UID) {
        try { localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(user)); } catch (e) {}
        return true;
    }

    const payload: Record<string, unknown> = {
      customDecks: user.customDecks,
      standardDeckOverrides: user.standardDeckOverrides,
      tournamentData: user.tournamentData,
      tournamentHistory: user.tournamentHistory,
      matchHistory: user.matchHistory
    };

    // 只有拿到有效的签到数据才提交；否则保持原样不发该字段，避免用空值覆盖服务器上的签到记录
    if (user.dailyCheckIn && Array.isArray(user.dailyCheckIn.checkedInDates)) {
      payload.dailyCheckIn = user.dailyCheckIn;
    }

    try {
      const response = await apiRequest('/api/user/update', 'save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        console.error("API save failed:", response.status);
        return false;
      }
      // 服务端可能返回非 JSON（网关错误页等）；只要不是明确的 success:false 就当作成功
      const text = await response.text();
      if (!text) return true;
      try {
        const data = JSON.parse(text) as { success?: boolean };
        return data?.success !== false;
      } catch (error) {
        return true;
      }
    } catch (error) {
      console.error("API save error:", error);
      return false;
    }
  }
};

export function migrateUserData(
  user: User,
  managedMasterLists?: MasterList[] | null,
  managedDrawPresetDecks?: CustomDeck[] | null
): User {
    let hasChanges = false;
    const migratedUser = JSON.parse(JSON.stringify(user)) as any;

    if (decodeLegacyUHexDeep(migratedUser).changed) {
        hasChanges = true;
    }

    if (!migratedUser.customDecks) {
        migratedUser.customDecks = [];
        hasChanges = true;
    }

    const requiredPresetFolders = getRequiredPresetFolders(managedDrawPresetDecks);

    if (!migratedUser.customFolders || migratedUser.customFolders.length === 0) {
        migratedUser.customFolders = requiredPresetFolders;
        hasChanges = true;
    } else {
        // Ensure all preset folders exist
        requiredPresetFolders.forEach(presetFolder => {
            if (!migratedUser.customFolders.some((f: any) => f.id === presetFolder.id)) {
                migratedUser.customFolders.push(presetFolder);
                hasChanges = true;
            }
        });
    }

    // Migrate legacy standard decks OUT of custom decks if they exist
    const stdIdsToRemove = ['std-universal', 'std-advanced', 'std-beginner'];
    const originalLength = migratedUser.customDecks.length;
    migratedUser.customDecks = migratedUser.customDecks.filter((d: CustomDeck) => !stdIdsToRemove.includes(d.id));
    if (migratedUser.customDecks.length !== originalLength) {
        hasChanges = true;
    }

    const customDecksBeforeMerge = JSON.stringify(migratedUser.customDecks ?? []);
    migratedUser.customDecks = mergeManagedDrawPresetDecksIntoUser(
      migratedUser as User,
      managedDrawPresetDecks
    ).customDecks;
    if (JSON.stringify(migratedUser.customDecks ?? []) !== customDecksBeforeMerge) {
        hasChanges = true;
    }

    const tournamentDataBeforeMerge = JSON.stringify(migratedUser.tournamentData ?? null);
    migratedUser.tournamentData = mergeManagedTournamentMasterListsIntoUser(
      migratedUser as User,
      managedMasterLists
    ).tournamentData;
    if (JSON.stringify(migratedUser.tournamentData ?? null) !== tournamentDataBeforeMerge) {
        hasChanges = true;
    }

    if (!migratedUser.tournamentHistory) {
        migratedUser.tournamentHistory = [];
        hasChanges = true;
    }

    if (!migratedUser.matchHistory) {
        migratedUser.matchHistory = [];
        hasChanges = true;
    }

    const normalizedWarmup = normalizeCko2026WarmupProfile(migratedUser.cko2026Warmup);
    const previousWarmupSnapshot = JSON.stringify(migratedUser.cko2026Warmup ?? createEmptyCko2026WarmupProfile());
    const normalizedWarmupSnapshot = JSON.stringify(normalizedWarmup);
    if (previousWarmupSnapshot !== normalizedWarmupSnapshot) {
        hasChanges = true;
    }
    migratedUser.cko2026Warmup = normalizedWarmup;

    if (hasChanges && user.uid !== GUEST_UID) {
        storage.save(migratedUser as User);
    }
    return migratedUser as User;
}
